/**
 * Product spreadsheet import. Companies export their catalog from Excel, Google Sheets or
 * their own system as CSV; these helpers read it in the browser and map the columns.
 */

/** RFC 4180-ish parser: commas or semicolons, quoted fields, "" escapes, CRLF. */
export function parseCsv(text: string): string[][] {
  const source = text.replace(/^﻿/, "");
  const firstLine = source.split(/\r?\n/, 1)[0] ?? "";
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (quoted) {
      if (ch === '"' && source[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && source[i + 1] === "\n") i++;
      row.push(field);
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "");

/** Accepted column names (accents, case and spaces ignored). */
const COLUMNS = {
  name: ["nombre", "producto", "name"],
  model: ["modelo", "sku", "codigo", "model"],
  category: ["categoria", "category"],
  description: ["descripcion", "description", "caracteristicas"],
  specs: ["especificaciones", "specs", "atributos"],
  images: ["fotos", "foto", "imagenes", "imagen", "images"],
  url: ["enlace", "url", "pagina", "link"],
} as const;

export interface CsvProduct {
  name: string;
  model?: string | null;
  category?: string | null;
  description?: string | null;
  specs: { label: string; value: string }[];
  images: { url: string }[];
  url?: string | null;
}

export interface CsvResult {
  items: CsvProduct[];
  skipped: number;
  missingName: boolean;
}

/** "Material: Acero; Capacidad: 12 tazas" -> specs. */
function parseSpecs(value: string) {
  return value
    .split(/[;|\n]/)
    .map((pair) => pair.split(":"))
    .filter((p) => p.length >= 2 && p[0]?.trim() && p.slice(1).join(":").trim())
    .map((p) => ({ label: (p[0] ?? "").trim().slice(0, 80), value: p.slice(1).join(":").trim().slice(0, 300) }));
}

export function readProductsCsv(text: string): CsvResult {
  const [header = [], ...rows] = parseCsv(text);
  const index: Partial<Record<keyof typeof COLUMNS, number>> = {};
  header.forEach((h, i) => {
    const key = (Object.keys(COLUMNS) as (keyof typeof COLUMNS)[]).find((k) =>
      (COLUMNS[k] as readonly string[]).includes(norm(h)),
    );
    if (key && index[key] === undefined) index[key] = i;
  });
  if (index.name === undefined) return { items: [], skipped: rows.length, missingName: true };
  const cell = (r: string[], k: keyof typeof COLUMNS) => (index[k] === undefined ? "" : (r[index[k]] ?? "").trim());
  let skipped = 0;
  const items: CsvProduct[] = [];
  for (const r of rows) {
    const name = cell(r, "name");
    if (!name) {
      skipped++;
      continue;
    }
    const url = cell(r, "url");
    items.push({
      name: name.slice(0, 200),
      model: cell(r, "model").slice(0, 80) || null,
      category: cell(r, "category").slice(0, 80) || null,
      description: cell(r, "description").slice(0, 4000) || null,
      specs: parseSpecs(cell(r, "specs")).slice(0, 60),
      images: cell(r, "images")
        .split(/[\s|,]+/)
        .filter((u) => /^https:\/\//.test(u))
        .slice(0, 20)
        .map((u) => ({ url: u })),
      url: /^https?:\/\//.test(url) ? url : null,
    });
  }
  return { items, skipped, missingName: false };
}

export const CSV_TEMPLATE = [
  "nombre,modelo,categoria,descripcion,especificaciones,fotos,enlace",
  '"Cafetera 12 tazas",CF-12DV,"Electro menor","Temporizador digital y jarra de vidrio","Capacidad: 12 tazas; Material: Acero inoxidable",https://tu-tienda.com/fotos/cf-12dv.jpg,https://tu-tienda.com/producto/cf-12dv',
].join("\r\n");
